<template>
  <div class="mx-5">
    <Breadcrumbs
      :links="[{ value: translatedSlugName, to: outfitsOverviewData?.slug }]"
    />
    <template v-if="preListingContent">
      <component
        :is="preContent.component"
        v-for="preContent in preListingContent"
        :key="preContent._uid"
        class="mb-4.5 w-full"
        :blok="preContent"
      />
    </template>
    <div
      class="mt-4 grid grid-cols-2 gap-x-1 gap-y-24 md:grid-cols-3 2xl:grid-cols-4"
      :class="{
        'mb-36': lastPage <= 1,
      }"
    >
      <ImageText
        v-for="(outfitData, idx) in outfitsData"
        :key="idx + '_' + outfitData._uid"
        :blok="outfitData"
      />
    </div>

    <AgsLazy placeholder-ratio="10/1">
      <template #lazy="{ visible }">
        <Pagination
          v-if="visible && lastPage > 1"
          :current-page="currentPage"
          :first-page="OUTFIT_FIRST_PAGE"
          :last-page="lastPage"
          class="!mt-12 mb-36"
        />
      </template>
    </AgsLazy>

    <template v-if="postListingContent">
      <component
        :is="postContent.component"
        v-for="postContent in postListingContent"
        :key="postContent._uid"
        class="w-full"
        :blok="postContent"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { defineOptions, computed } from 'vue'
import type { StoryblokStory } from 'storyblok-generate-ts'
import { definePageMeta, defineI18nRoute } from '#imports'
import { fetchOnNavigation } from '#layers/base/utils/loading'
import useStoryMetaData from '~/composables/useStoryMetaData'
import useStaticMetaLinks from '#layers/base/composables/useStaticMetaLinks'
import useTranslatedSlug from '~/composables/useTranslatedSlug'
import useOutfitsFolder from '#layers/base/composables/useOutfitsFolder'
import { OUTFIT_FIRST_PAGE } from '#layers/base/constants/pagination'
import type { SbContentPage } from '~/cms/storyblok/types/storyblok'

useRouteCache((helper) => {
  helper.setMaxAge(3600).setCacheable()
})

const {
  outfitsData,
  outfitsOverviewData,
  fetch,
  fetching,
  currentPage,
  lastPage,
} = useOutfitsFolder()

const { name: translatedSlugName } = useTranslatedSlug(
  outfitsOverviewData as unknown as Ref<StoryblokStory<SbContentPage>>,
)

const metaLinks = useStaticMetaLinks('outfits')

const metaDataExtensions = computed(() => ({
  link: metaLinks.value,
}))

useStoryMetaData({
  data: outfitsOverviewData,
  fetching,
  extensions: metaDataExtensions,
})

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    de: '/outfits',
    at: '/outfits',
    ch: '/outfits',
    'fr-ch': '/ensembles',
    fr: '/ensembles',
    'fr-be': '/ensembles',
    be: '/outfits',
    nl: '/outfits',
    fi: '/asukokonaisuudet',
  },
})

await fetchOnNavigation(async () => {
  await fetch(currentPage.value)
})

const preListingContent = computed(
  () => outfitsOverviewData?.value?.content?.preContent || [],
)

const postListingContent = computed(
  () => outfitsOverviewData?.value?.content?.postContent || [],
)

defineOptions({ name: 'OutfitsPages' })
definePageMeta({ pageType: 'outfit_overview' })
</script>
